import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Upload, Music, Trash2, Edit2, Play, Square, Mic, ChevronDown, ChevronRight, Clock, Settings, Plus, FolderUp, X, Check, FileAudio } from 'lucide-react';
import { useBusiness } from '../context/BusinessContext';
import { useTheme } from '../context/ThemeContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { Progress } from '@/components/ui/progress';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper function to get full image URL (handles both relative paths and full URLs)
const getImageUrl = (imagePath) => {
  if (!imagePath) return null;
  // If it's already a full URL (starts with http), return as-is
  if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
    // Check if it's an old preview URL that needs updating
    if (imagePath.includes('.preview.emergentagent.com/api/')) {
      // Extract the relative path and use current backend URL
      const relativePath = imagePath.split('.preview.emergentagent.com')[1];
      return `${BACKEND_URL}${relativePath}`;
    }
    return imagePath;
  }
  // If it's a relative path (starts with /), prepend backend URL
  if (imagePath.startsWith('/')) {
    return `${BACKEND_URL}${imagePath}`;
  }
  return imagePath;
};

// Genre images and colors
const GENRE_CONFIG = {
  'Pop': {
    image: 'https://images.unsplash.com/photo-1514525253161-7a46d19cd819?w=400&h=300&fit=crop',
    color: 'from-pink-500 to-rose-600',
    bgColor: 'bg-pink-500/10',
    borderColor: 'border-pink-500/30'
  },
  'Rock': {
    image: 'https://images.unsplash.com/photo-1498038432885-c6f3f1b912ee?w=400&h=300&fit=crop',
    color: 'from-red-500 to-orange-600',
    bgColor: 'bg-red-500/10',
    borderColor: 'border-red-500/30'
  },
  'Jazz': {
    image: 'https://images.unsplash.com/photo-1511192336575-5a79af67a629?w=400&h=300&fit=crop',
    color: 'from-amber-500 to-yellow-600',
    bgColor: 'bg-amber-500/10',
    borderColor: 'border-amber-500/30'
  },
  'Classical': {
    image: 'https://images.unsplash.com/photo-1507838153414-b4b713384a76?w=400&h=300&fit=crop',
    color: 'from-violet-500 to-purple-600',
    bgColor: 'bg-violet-500/10',
    borderColor: 'border-violet-500/30'
  },
  'Electronic': {
    image: 'https://images.unsplash.com/photo-1571266028243-e4733b0f0bb0?w=400&h=300&fit=crop',
    color: 'from-cyan-500 to-blue-600',
    bgColor: 'bg-cyan-500/10',
    borderColor: 'border-cyan-500/30'
  },
  'Hip Hop': {
    image: 'https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=400&h=300&fit=crop',
    color: 'from-emerald-500 to-teal-600',
    bgColor: 'bg-emerald-500/10',
    borderColor: 'border-emerald-500/30'
  },
  'Country': {
    image: 'https://images.unsplash.com/photo-1510915361894-db8b60106cb1?w=400&h=300&fit=crop',
    color: 'from-orange-500 to-amber-600',
    bgColor: 'bg-orange-500/10',
    borderColor: 'border-orange-500/30'
  },
  'R&B': {
    image: 'https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=400&h=300&fit=crop',
    color: 'from-purple-500 to-pink-600',
    bgColor: 'bg-purple-500/10',
    borderColor: 'border-purple-500/30'
  },
  'Ambient': {
    image: 'https://images.unsplash.com/photo-1518972559570-7cc1309f3229?w=400&h=300&fit=crop',
    color: 'from-slate-500 to-slate-600',
    bgColor: 'bg-slate-500/10',
    borderColor: 'border-slate-500/30'
  },
  'Other': {
    image: 'https://images.unsplash.com/photo-1511379938547-c1f69419868d?w=400&h=300&fit=crop',
    color: 'from-gray-500 to-gray-600',
    bgColor: 'bg-gray-500/10',
    borderColor: 'border-gray-500/30'
  }
};

// Announcement type config
const ANNOUNCEMENT_CONFIG = {
  'Store Opening': { color: 'from-emerald-500 to-green-600', icon: '🌅', bgColor: 'bg-emerald-500/10', borderColor: 'border-emerald-500/50', image: 'https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=400&h=400&fit=crop' },
  'Store Closing': { color: 'from-indigo-500 to-blue-600', icon: '🌙', bgColor: 'bg-indigo-500/10', borderColor: 'border-indigo-500/50', image: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=400&h=400&fit=crop' },
  'Promotion': { color: 'from-amber-500 to-orange-600', icon: '🎉', bgColor: 'bg-amber-500/10', borderColor: 'border-amber-500/50', image: 'https://images.unsplash.com/photo-1607083206869-4c7672e72a8a?w=400&h=400&fit=crop' },
  'Safety': { color: 'from-red-500 to-rose-600', icon: '⚠️', bgColor: 'bg-red-500/10', borderColor: 'border-red-500/50', image: 'https://images.unsplash.com/photo-1584438784894-089d6a62b8fa?w=400&h=400&fit=crop' },
  'General': { color: 'from-slate-500 to-gray-600', icon: '📢', bgColor: 'bg-slate-500/10', borderColor: 'border-slate-500/50', image: 'https://images.unsplash.com/photo-1478737270239-2f02b77fc618?w=400&h=400&fit=crop' },
  'Holiday': { color: 'from-pink-500 to-rose-600', icon: '🎄', bgColor: 'bg-pink-500/10', borderColor: 'border-pink-500/50', image: 'https://images.unsplash.com/photo-1512389142860-9c449e58a814?w=400&h=400&fit=crop' },
  'Event': { color: 'from-violet-500 to-purple-600', icon: '🎪', bgColor: 'bg-violet-500/10', borderColor: 'border-violet-500/50', image: 'https://images.unsplash.com/photo-1492684223066-81342ee5ff30?w=400&h=400&fit=crop' },
  'Other': { color: 'from-gray-500 to-slate-600', icon: '📋', bgColor: 'bg-gray-500/10', borderColor: 'border-gray-500/50', image: 'https://images.unsplash.com/photo-1516280440614-37939bbacd81?w=400&h=400&fit=crop' }
};

const MusicLibrary = () => {
  const { selectedBusinessId, isSuperAdmin } = useBusiness();
  const { isLight } = useTheme();
  const [songs, setSongs] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [genres, setGenres] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadAnnouncementOpen, setUploadAnnouncementOpen] = useState(false);
  const [uploadBackgroundMusicOpen, setUploadBackgroundMusicOpen] = useState(false);
  const [backgroundMusicData, setBackgroundMusicData] = useState({ file: null, name: '' });
  const [editOpen, setEditOpen] = useState(false);
  const [selectedSong, setSelectedSong] = useState(null);
  const [expandedGenres, setExpandedGenres] = useState({});
  const [expandedAnnouncementTypes, setExpandedAnnouncementTypes] = useState({});
  
  // Background music list for TTS
  const [backgroundMusic, setBackgroundMusic] = useState([]);
  
  // Genre management state
  const [genreDialogOpen, setGenreDialogOpen] = useState(false);
  const [editingGenre, setEditingGenre] = useState(null);
  const [newGenreName, setNewGenreName] = useState('');
  const [newGenreImage, setNewGenreImage] = useState('');
  const [newGenreColor, setNewGenreColor] = useState('from-gray-500 to-gray-600');
  
  // Bulk upload state
  const [bulkUploadOpen, setBulkUploadOpen] = useState(false);
  const [bulkUploadStep, setBulkUploadStep] = useState(1); // 1: select genre, 2: upload files
  const [bulkUploadGenre, setBulkUploadGenre] = useState('');
  const [bulkUploadNewGenre, setBulkUploadNewGenre] = useState('');
  const [bulkUploadFiles, setBulkUploadFiles] = useState([]);
  const [bulkUploadProgress, setBulkUploadProgress] = useState(0);
  const [bulkUploading, setBulkUploading] = useState(false);
  const bulkFileInputRef = useRef(null);
  
  // Audio playback state
  const [playingId, setPlayingId] = useState(null);
  const [playingType, setPlayingType] = useState(null); // 'song' or 'announcement'
  const [audioProgress, setAudioProgress] = useState(0); // 0 to 100
  const [audioDuration, setAudioDuration] = useState(0);
  const [audioCurrentTime, setAudioCurrentTime] = useState(0);
  const audioRef = useRef(null);
  
  const [uploadData, setUploadData] = useState({
    file: null,
    name: '',
    artist: '',
    genre: '',
    duration: 0
  });
  const [announcementData, setAnnouncementData] = useState({
    file: null,
    name: '',
    genre: '',
    duration: 0
  });

  // Announcement types state - editable
  const [announcementTypes, setAnnouncementTypes] = useState([
    'Store Opening', 'Store Closing', 'Promotion', 'Safety', 'General', 'Holiday', 'Event', 'Other'
  ]);
  
  // Announcement type management state
  const [announcementTypeDialogOpen, setAnnouncementTypeDialogOpen] = useState(false);
  const [editingAnnouncementType, setEditingAnnouncementType] = useState(null);
  const [newAnnouncementTypeName, setNewAnnouncementTypeName] = useState('');
  const [newAnnouncementTypeImage, setNewAnnouncementTypeImage] = useState('');
  const [newAnnouncementTypeColor, setNewAnnouncementTypeColor] = useState('from-amber-500 to-orange-600');
  
  // Custom announcement type configs (for user-added types with custom images)
  const [customAnnouncementConfigs, setCustomAnnouncementConfigs] = useState({});
  
  // Color options for genres
  const colorOptions = [
    { value: 'from-pink-500 to-rose-600', label: 'Pink' },
    { value: 'from-red-500 to-orange-600', label: 'Red' },
    { value: 'from-amber-500 to-yellow-600', label: 'Amber' },
    { value: 'from-violet-500 to-purple-600', label: 'Violet' },
    { value: 'from-cyan-500 to-blue-600', label: 'Cyan' },
    { value: 'from-emerald-500 to-teal-600', label: 'Emerald' },
    { value: 'from-orange-500 to-amber-600', label: 'Orange' },
    { value: 'from-purple-500 to-pink-600', label: 'Purple' },
    { value: 'from-slate-500 to-slate-600', label: 'Slate' },
    { value: 'from-gray-500 to-gray-600', label: 'Gray' },
  ];

  useEffect(() => {
    fetchData();
    fetchGenres();
    fetchBackgroundMusic();
  }, [selectedBusinessId]);

  // Cleanup audio on unmount
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const businessQuery = selectedBusinessId ? `?business_id=${selectedBusinessId}` : '';
      
      const [songsRes, announcementsRes] = await Promise.all([
        axios.get(`${API}/songs`, { headers }),
        axios.get(`${API}/announcements${businessQuery}`, { headers })
      ]);
      setSongs(songsRes.data);
      setAnnouncements(announcementsRes.data);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const fetchBackgroundMusic = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/background-music`, { headers });
      setBackgroundMusic(response.data || []);
    } catch (error) {
      console.error('Failed to fetch background music:', error);
    }
  };

  const fetchGenres = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/genres`, { headers });
      setGenres(response.data);
    } catch (error) {
      console.error('Failed to fetch genres:', error);
    }
  };

  // Genre management functions
  const handleAddGenre = async () => {
    if (!newGenreName.trim()) {
      toast.error('Please enter a genre name');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('name', newGenreName.trim());
      if (newGenreImage) formData.append('image', newGenreImage);
      formData.append('color', newGenreColor);
      
      await axios.post(`${API}/genres`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Genre added successfully');
      fetchGenres();
      resetGenreForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add genre');
    }
  };

  const handleEditGenre = async () => {
    if (!newGenreName.trim() || !editingGenre) {
      toast.error('Please enter a genre name');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('name', newGenreName.trim());
      if (newGenreImage) formData.append('image', newGenreImage);
      formData.append('color', newGenreColor);
      
      await axios.put(`${API}/genres/${editingGenre.id}`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Genre updated successfully');
      fetchGenres();
      fetchData(); // Refresh songs to show updated genre names
      resetGenreForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update genre');
    }
  };

  const handleDeleteGenre = async (genre) => {
    if (!confirm(`Are you sure you want to delete "${genre.name}"?`)) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/genres/${genre.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Genre deleted successfully');
      fetchGenres();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete genre');
    }
  };

  const startEditGenre = (genre) => {
    setEditingGenre(genre);
    setNewGenreName(genre.name);
    setNewGenreImage(genre.image || '');
    setNewGenreColor(genre.color || 'from-gray-500 to-gray-600');
  };

  const resetGenreForm = () => {
    setEditingGenre(null);
    setNewGenreName('');
    setNewGenreImage('');
    setNewGenreColor('from-gray-500 to-gray-600');
  };

  // Announcement Type Management Functions
  const handleAddAnnouncementType = () => {
    if (!newAnnouncementTypeName.trim()) {
      toast.error('Please enter a type name');
      return;
    }
    if (announcementTypes.includes(newAnnouncementTypeName.trim())) {
      toast.error('This type already exists');
      return;
    }
    
    const newType = newAnnouncementTypeName.trim();
    setAnnouncementTypes(prev => [...prev, newType]);
    
    // Save custom config if image or color provided
    if (newAnnouncementTypeImage || newAnnouncementTypeColor !== 'from-amber-500 to-orange-600') {
      setCustomAnnouncementConfigs(prev => ({
        ...prev,
        [newType]: {
          color: newAnnouncementTypeColor,
          image: newAnnouncementTypeImage || 'https://images.unsplash.com/photo-1516280440614-37939bbacd81?w=400&h=400&fit=crop',
          bgColor: 'bg-amber-500/10',
          borderColor: 'border-amber-500/50'
        }
      }));
    }
    
    toast.success(`Added announcement type: ${newType}`);
    resetAnnouncementTypeForm();
  };

  const handleEditAnnouncementType = () => {
    if (!newAnnouncementTypeName.trim()) {
      toast.error('Please enter a type name');
      return;
    }
    
    const newName = newAnnouncementTypeName.trim();
    const oldName = editingAnnouncementType;
    
    // Update the type name in the list
    setAnnouncementTypes(prev => prev.map(t => t === oldName ? newName : t));
    
    // Update custom config
    setCustomAnnouncementConfigs(prev => {
      const newConfigs = { ...prev };
      if (oldName !== newName && newConfigs[oldName]) {
        newConfigs[newName] = newConfigs[oldName];
        delete newConfigs[oldName];
      }
      newConfigs[newName] = {
        color: newAnnouncementTypeColor,
        image: newAnnouncementTypeImage || ANNOUNCEMENT_CONFIG[oldName]?.image || 'https://images.unsplash.com/photo-1516280440614-37939bbacd81?w=400&h=400&fit=crop',
        bgColor: 'bg-amber-500/10',
        borderColor: 'border-amber-500/50'
      };
      return newConfigs;
    });
    
    toast.success(`Updated announcement type: ${newName}`);
    resetAnnouncementTypeForm();
  };

  const handleDeleteAnnouncementType = (typeName) => {
    const typeAnnouncements = announcements.filter(a => (a.genre || 'General') === typeName);
    if (typeAnnouncements.length > 0) {
      toast.error(`Cannot delete: ${typeAnnouncements.length} announcement(s) are using this type`);
      return;
    }
    
    if (!confirm(`Are you sure you want to delete "${typeName}"?`)) return;
    
    setAnnouncementTypes(prev => prev.filter(t => t !== typeName));
    setCustomAnnouncementConfigs(prev => {
      const newConfigs = { ...prev };
      delete newConfigs[typeName];
      return newConfigs;
    });
    
    toast.success(`Deleted announcement type: ${typeName}`);
  };

  const resetAnnouncementTypeForm = () => {
    setEditingAnnouncementType(null);
    setNewAnnouncementTypeName('');
    setNewAnnouncementTypeImage('');
    setNewAnnouncementTypeColor('from-amber-500 to-orange-600');
  };

  const getAnnouncementConfig = (type) => {
    // Check custom configs first, then fall back to defaults
    if (customAnnouncementConfigs[type]) {
      return customAnnouncementConfigs[type];
    }
    return ANNOUNCEMENT_CONFIG[type] || ANNOUNCEMENT_CONFIG['Other'];
  };

  // Bulk Upload Functions
  const extractSongName = (filename) => {
    // Remove extension and clean up filename
    let name = filename.replace(/\.[^/.]+$/, ''); // Remove extension
    // Replace underscores and hyphens with spaces
    name = name.replace(/[_-]/g, ' ');
    // Capitalize first letter of each word
    name = name.replace(/\b\w/g, c => c.toUpperCase());
    return name.trim();
  };

  const handleBulkFileSelect = (e) => {
    const files = Array.from(e.target.files);
    const mp3Files = files.filter(f => f.name.toLowerCase().endsWith('.mp3'));
    
    if (mp3Files.length === 0) {
      toast.error('Please select MP3 files');
      return;
    }
    
    if (mp3Files.length !== files.length) {
      toast.warning(`${files.length - mp3Files.length} non-MP3 files were ignored`);
    }

    // Process files immediately with default duration
    // We'll get actual duration asynchronously
    const processedFiles = mp3Files.map(file => ({
      file,
      name: extractSongName(file.name),
      artist: 'Yume Loop',
      duration: 180, // default 3 minutes, will be updated
      status: 'pending'
    }));
    
    setBulkUploadFiles(processedFiles);

    // Try to get actual duration for each file (non-blocking)
    processedFiles.forEach((fileData, index) => {
      const audio = new Audio();
      const objectUrl = URL.createObjectURL(fileData.file);
      
      const cleanup = () => {
        URL.revokeObjectURL(objectUrl);
      };

      audio.onloadedmetadata = () => {
        const duration = Math.round(audio.duration);
        if (duration > 0) {
          setBulkUploadFiles(prev => {
            const updated = [...prev];
            if (updated[index]) {
              updated[index] = { ...updated[index], duration };
            }
            return updated;
          });
        }
        cleanup();
      };

      audio.onerror = () => {
        cleanup();
      };

      // Set timeout to avoid hanging
      setTimeout(() => {
        if (audio.readyState < 2) {
          cleanup();
        }
      }, 5000);

      audio.src = objectUrl;
    });
  };

  const updateBulkFile = (index, field, value) => {
    setBulkUploadFiles(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const removeBulkFile = (index) => {
    setBulkUploadFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleBulkUpload = async () => {
    const genre = bulkUploadGenre || bulkUploadNewGenre;
    if (!genre) {
      toast.error('Please select or create a genre');
      return;
    }
    if (bulkUploadFiles.length === 0) {
      toast.error('Please select files to upload');
      return;
    }

    setBulkUploading(true);
    setBulkUploadProgress(0);
    
    const token = localStorage.getItem('token');
    let successCount = 0;
    let errorCount = 0;

    console.log('Starting bulk upload:', { genre, fileCount: bulkUploadFiles.length });

    // If creating a new genre, do that first
    if (bulkUploadNewGenre && !genres.find(g => g.name === bulkUploadNewGenre)) {
      try {
        console.log('Creating new genre:', bulkUploadNewGenre);
        const formData = new FormData();
        formData.append('name', bulkUploadNewGenre.trim());
        formData.append('color', 'from-violet-500 to-purple-600');
        await axios.post(`${API}/genres`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        await fetchGenres();
        console.log('Genre created successfully');
      } catch (error) {
        console.error('Failed to create genre:', error);
        toast.error('Failed to create genre');
        setBulkUploading(false);
        return;
      }
    }

    // Upload each file
    for (let i = 0; i < bulkUploadFiles.length; i++) {
      const fileData = bulkUploadFiles[i];
      console.log(`Uploading file ${i + 1}/${bulkUploadFiles.length}:`, fileData.name);
      
      updateBulkFile(i, 'status', 'uploading');

      try {
        const formData = new FormData();
        formData.append('file', fileData.file);
        formData.append('name', fileData.name);
        formData.append('artist', fileData.artist);
        formData.append('genre', genre);
        formData.append('duration', String(fileData.duration));

        console.log('Sending request with formData:', {
          name: fileData.name,
          artist: fileData.artist,
          genre: genre,
          duration: fileData.duration,
          fileName: fileData.file.name
        });

        const response = await axios.post(`${API}/songs/upload`, formData, {
          headers: { 
            Authorization: `Bearer ${token}`
          }
        });

        console.log('Upload successful:', response.data);
        updateBulkFile(i, 'status', 'success');
        successCount++;
      } catch (error) {
        console.error('Upload failed:', error.response?.data || error.message);
        updateBulkFile(i, 'status', 'error');
        errorCount++;
      }

      setBulkUploadProgress(Math.round(((i + 1) / bulkUploadFiles.length) * 100));
    }

    setBulkUploading(false);
    console.log('Bulk upload complete:', { successCount, errorCount });
    
    if (successCount > 0) {
      toast.success(`Successfully uploaded ${successCount} song${successCount > 1 ? 's' : ''}`);
      fetchData();
    }
    if (errorCount > 0) {
      toast.error(`Failed to upload ${errorCount} song${errorCount > 1 ? 's' : ''}`);
    }

    // Reset after a short delay to show results
    setTimeout(() => {
      if (errorCount === 0) {
        resetBulkUpload();
      }
    }, 2000);
  };

  const resetBulkUpload = () => {
    setBulkUploadOpen(false);
    setBulkUploadStep(1);
    setBulkUploadGenre('');
    setBulkUploadNewGenre('');
    setBulkUploadFiles([]);
    setBulkUploadProgress(0);
    setBulkUploading(false);
  };

  // Get genre names for dropdowns
  const genreNames = genres.map(g => g.name);

  // Group songs by genre
  const songsByGenre = genreNames.reduce((acc, genreName) => {
    acc[genreName] = songs.filter(song => song.genre === genreName);
    return acc;
  }, {});

  // Helper to get genre config (from fetched genres or fallback to GENRE_CONFIG)
  const getGenreConfig = (genreName) => {
    const genre = genres.find(g => g.name === genreName);
    if (genre) {
      return {
        image: genre.image,
        color: genre.color,
        bgColor: `bg-${genre.color.split(' ')[0].replace('from-', '').split('-')[0]}-500/10`,
        borderColor: `border-${genre.color.split(' ')[0].replace('from-', '').split('-')[0]}-500/30`
      };
    }
    return GENRE_CONFIG[genreName] || GENRE_CONFIG['Other'];
  };

  // Group announcements by type
  const announcementsByType = announcementTypes.reduce((acc, type) => {
    acc[type] = announcements.filter(a => (a.genre || 'General') === type);
    return acc;
  }, {});

  const toggleGenre = (genreName) => {
    setExpandedGenres(prev => ({ ...prev, [genreName]: !prev[genreName] }));
  };

  const toggleAnnouncementType = (type) => {
    setExpandedAnnouncementTypes(prev => ({ ...prev, [type]: !prev[type] }));
  };

  // Play/Stop functionality
  const handlePlayStop = (item, type) => {
    // If clicking on currently playing item, stop it
    if (playingId === item.id && playingType === type) {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      setPlayingId(null);
      setPlayingType(null);
      setAudioProgress(0);
      setAudioCurrentTime(0);
      setAudioDuration(0);
      return;
    }

    // Stop any currently playing audio
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }

    // Play new audio
    const audio = new Audio(`${BACKEND_URL}${item.file_path}`);
    audioRef.current = audio;
    
    // Track progress
    audio.ontimeupdate = () => {
      if (audio.duration) {
        setAudioProgress((audio.currentTime / audio.duration) * 100);
        setAudioCurrentTime(audio.currentTime);
      }
    };
    
    audio.onloadedmetadata = () => {
      setAudioDuration(audio.duration);
    };
    
    audio.onended = () => {
      setPlayingId(null);
      setPlayingType(null);
      setAudioProgress(0);
      setAudioCurrentTime(0);
      setAudioDuration(0);
      audioRef.current = null;
    };

    audio.onerror = () => {
      toast.error('Failed to play audio');
      setPlayingId(null);
      setPlayingType(null);
      setAudioProgress(0);
      audioRef.current = null;
    };

    audio.play().then(() => {
      setPlayingId(item.id);
      setPlayingType(type);
    }).catch(() => {
      toast.error('Failed to play audio');
    });
  };
  
  // Seek to position in audio
  const handleSeek = (e, songDuration) => {
    if (!audioRef.current) return;
    
    const rect = e.currentTarget.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const percentage = clickX / rect.width;
    const seekTime = percentage * audioRef.current.duration;
    
    audioRef.current.currentTime = seekTime;
    setAudioProgress(percentage * 100);
    setAudioCurrentTime(seekTime);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.name.endsWith('.mp3')) {
      toast.error('Only MP3 files are allowed');
      return;
    }

    const audio = new Audio();
    audio.src = URL.createObjectURL(file);
    audio.onloadedmetadata = () => {
      setUploadData(prev => ({
        ...prev,
        file,
        duration: Math.round(audio.duration)
      }));
    };
  };

  const handleAnnouncementFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.name.endsWith('.mp3')) {
      toast.error('Only MP3 files are allowed');
      return;
    }
    
    const audio = new Audio();
    audio.src = URL.createObjectURL(file);
    audio.onloadedmetadata = () => {
      setAnnouncementData(prev => ({ 
        ...prev, 
        file,
        duration: Math.round(audio.duration)
      }));
    };
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!uploadData.file) {
      toast.error('Please select a file');
      return;
    }

    const formData = new FormData();
    formData.append('file', uploadData.file);
    formData.append('name', uploadData.name);
    formData.append('artist', uploadData.artist);
    formData.append('genre', uploadData.genre);
    formData.append('duration', uploadData.duration);

    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/songs/upload`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success('Song uploaded successfully');
      setUploadOpen(false);
      setUploadData({ file: null, name: '', artist: '', genre: '', duration: 0 });
      fetchData();
    } catch (error) {
      const errorMsg = error.response?.data?.detail;
      if (typeof errorMsg === 'string') {
        toast.error(errorMsg);
      } else {
        toast.error('Failed to upload song');
      }
    }
  };

  const handleUploadAnnouncement = async (e) => {
    e.preventDefault();
    if (!announcementData.file) {
      toast.error('Please select a file');
      return;
    }

    const formData = new FormData();
    formData.append('file', announcementData.file);
    formData.append('name', announcementData.name);
    formData.append('genre', announcementData.genre || 'General');
    formData.append('duration', announcementData.duration || 30);
    if (selectedBusinessId) {
      formData.append('business_id', selectedBusinessId);
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/announcements/upload`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success('Announcement uploaded successfully');
      setUploadAnnouncementOpen(false);
      setAnnouncementData({ file: null, name: '', genre: '', duration: 0 });
      fetchData();
    } catch (error) {
      const errorMsg = error.response?.data?.detail;
      if (typeof errorMsg === 'string') {
        toast.error(errorMsg);
      } else {
        toast.error('Failed to upload announcement');
      }
    }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    if (!selectedSong) return;

    const formData = new FormData();
    formData.append('name', selectedSong.name);
    formData.append('artist', selectedSong.artist);
    formData.append('genre', selectedSong.genre);

    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/songs/${selectedSong.id}`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Song updated successfully');
      setEditOpen(false);
      setSelectedSong(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to update song');
    }
  };

  const handleDeleteSong = async (songId) => {
    if (!window.confirm('Are you sure you want to delete this song?')) return;

    // Stop if currently playing
    if (playingId === songId && playingType === 'song') {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      setPlayingId(null);
      setPlayingType(null);
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/songs/${songId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Song deleted successfully');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete song');
    }
  };

  const handleDeleteAnnouncement = async (announcementId) => {
    if (!window.confirm('Are you sure you want to delete this announcement?')) return;

    // Stop if currently playing
    if (playingId === announcementId && playingType === 'announcement') {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      setPlayingId(null);
      setPlayingType(null);
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/announcements/${announcementId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Announcement deleted successfully');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete announcement');
    }
  };

  const handleUploadBackgroundMusic = async (e) => {
    e.preventDefault();
    if (!backgroundMusicData.file) {
      toast.error('Please select a file');
      return;
    }
    if (!backgroundMusicData.name.trim()) {
      toast.error('Please enter a name');
      return;
    }

    const formData = new FormData();
    formData.append('file', backgroundMusicData.file);
    formData.append('name', backgroundMusicData.name.trim());

    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/background-music/upload`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success('Background music uploaded successfully');
      setUploadBackgroundMusicOpen(false);
      setBackgroundMusicData({ file: null, name: '' });
      fetchBackgroundMusic();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload background music');
    }
  };

  const handleDeleteBackgroundMusic = async (id) => {
    if (!window.confirm('Are you sure you want to delete this background music?')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/background-music/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Background music deleted successfully');
      fetchBackgroundMusic();
    } catch (error) {
      toast.error('Failed to delete background music');
    }
  };

  const formatDuration = (seconds) => {
    const totalSecs = Math.round(seconds || 0);
    const mins = Math.floor(totalSecs / 60);
    const secs = totalSecs % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-slate-400">Loading...</div>
      </div>
    );
  }

  return (
    <div className={`p-8 min-h-screen ${isLight ? 'bg-[#faf8f5]' : ''}`}>
      <div className="max-w-7xl mx-auto space-y-6">
        <div>
          <h1 className={`text-3xl font-bold mb-2 ${isLight ? 'text-gray-800' : 'text-white'}`} data-testid="music-library-title">
            Music & Announcements Library
          </h1>
          <p className={isLight ? 'text-gray-500' : 'text-slate-400'}>Browse by genre • Click play to preview tracks</p>
        </div>

        <Tabs defaultValue="songs" className="w-full">
          <TabsList className={isLight ? 'bg-white border border-[#ebe7e0]' : 'bg-slate-900 border border-slate-800'}>
            <TabsTrigger value="songs" className={`${isLight ? 'data-[state=active]:bg-[#e94560] data-[state=active]:text-white' : 'data-[state=active]:bg-rose-600'}`}>
              <Music className="w-4 h-4 mr-2" />
              Songs ({songs.length})
            </TabsTrigger>
            <TabsTrigger value="announcements" className={`${isLight ? 'data-[state=active]:bg-[#e94560] data-[state=active]:text-white' : 'data-[state=active]:bg-amber-600'}`}>
              <Mic className="w-4 h-4 mr-2" />
              Announcements ({announcements.length})
            </TabsTrigger>
          </TabsList>

          {/* SONGS TAB - Genre Tiles View */}
          <TabsContent value="songs" className="space-y-6 mt-6">
            {/* Action buttons - Super Admin Only */}
            {isSuperAdmin && (
              <div className="flex justify-end gap-3">
                {/* Manage Genres Button - Super Admin Only */}
                <Dialog open={genreDialogOpen} onOpenChange={(open) => {
                  setGenreDialogOpen(open);
                  if (!open) resetGenreForm();
                }}>
                  <DialogTrigger asChild>
                    <Button
                      data-testid="manage-genres-button"
                      variant="outline"
                      className={`rounded-full px-6 ${isLight ? 'border-[#ebe7e0] text-gray-600 hover:bg-gray-50' : 'border-slate-700 text-slate-300 hover:bg-slate-800'}`}
                    >
                      <Settings className="w-4 h-4 mr-2" />
                      Manage Genres
                    </Button>
                  </DialogTrigger>
                  <DialogContent className={`max-w-2xl max-h-[80vh] overflow-y-auto ${isLight ? 'bg-white border-[#ebe7e0] text-gray-800' : 'bg-slate-900 border-slate-800 text-white'}`}>
                    <DialogHeader>
                      <DialogTitle className={isLight ? 'text-gray-800' : ''}>Manage Genres</DialogTitle>
                    </DialogHeader>
                    
                    {/* Add/Edit Genre Form */}
                    <div className={`space-y-4 border-b pb-4 ${isLight ? 'border-[#ebe7e0]' : 'border-slate-700'}`}>
                      <h3 className={`text-sm font-medium ${isLight ? 'text-gray-600' : 'text-slate-300'}`}>
                        {editingGenre ? 'Edit Genre' : 'Add New Genre'}
                      </h3>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className={isLight ? 'text-gray-600' : ''}>Genre Name</Label>
                          <Input
                            value={newGenreName}
                            onChange={(e) => setNewGenreName(e.target.value)}
                            placeholder="e.g., Lounge, Indie"
                            className={`mt-1 ${isLight ? 'bg-[#faf8f5] border-[#ebe7e0] text-gray-800' : 'bg-slate-950 border-slate-700 text-white'}`}
                          />
                        </div>
                        <div>
                          <Label className={isLight ? 'text-gray-600' : ''}>Color Theme</Label>
                          <Select value={newGenreColor} onValueChange={setNewGenreColor}>
                            <SelectTrigger className={`mt-1 ${isLight ? 'bg-[#faf8f5] border-[#ebe7e0] text-gray-800' : 'bg-slate-950 border-slate-700 text-white'}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className={isLight ? 'bg-white border-[#ebe7e0]' : 'bg-slate-900 border-slate-700'}>
                              {colorOptions.map(opt => (
                                <SelectItem key={opt.value} value={opt.value}>
                                  <div className="flex items-center gap-2">
                                    <div className={`w-4 h-4 rounded bg-gradient-to-r ${opt.value}`} />
                                    {opt.label}
                                  </div>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div>
                        <Label className={isLight ? 'text-gray-600' : ''}>Genre Image</Label>
                        <div className="mt-1 space-y-2">
                          <Input
                            value={newGenreImage}
                            onChange={(e) => setNewGenreImage(e.target.value)}
                            placeholder="Paste image URL or upload below..."
                            className={isLight ? 'bg-[#faf8f5] border-[#ebe7e0] text-gray-800' : 'bg-slate-950 border-slate-700 text-white'}
                          />
                          <div className="flex items-center gap-2">
                            <input
                              type="file"
                              accept="image/*"
                              id="genre-image-upload"
                              className="hidden"
                              onChange={async (e) => {
                                const file = e.target.files?.[0];
                                if (!file) return;
                                
                                // Create FormData and upload
                                const formData = new FormData();
                                formData.append('file', file);
                                
                                try {
                                  const token = localStorage.getItem('token');
                                  const response = await fetch(`${BACKEND_URL}/api/upload-image`, {
                                    method: 'POST',
                                    headers: { 'Authorization': `Bearer ${token}` },
                                    body: formData
                                  });
                                  
                                  if (response.ok) {
                                    const data = await response.json();
                                    // Store only the relative path, not the full URL
                                    setNewGenreImage(data.file_path);
                                    toast.success('Image uploaded successfully');
                                  } else {
                                    toast.error('Failed to upload image');
                                  }
                                } catch (error) {
                                  toast.error('Upload failed');
                                }
                              }}
                            />
                            <label
                              htmlFor="genre-image-upload"
                              className={`flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer text-sm transition-colors ${isLight ? 'bg-gray-100 hover:bg-gray-200 text-gray-600' : 'bg-slate-800 hover:bg-slate-700 text-slate-300'}`}
                            >
                              <Upload className="w-4 h-4" />
                              Upload Image
                            </label>
                            {newGenreImage && (
                              <div className="relative w-12 h-12 rounded overflow-hidden">
                                <img src={getImageUrl(newGenreImage)} alt="Preview" className="w-full h-full object-cover" />
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        {editingGenre ? (
                          <>
                            <Button onClick={handleEditGenre} className={isLight ? 'bg-[#e94560] hover:bg-[#d63d56]' : 'bg-rose-600 hover:bg-rose-500'}>
                              Save Changes
                            </Button>
                            <Button variant="outline" onClick={resetGenreForm} className={isLight ? 'border-[#ebe7e0]' : 'border-slate-700'}>
                              Cancel
                            </Button>
                          </>
                        ) : (
                          <Button onClick={handleAddGenre} className={isLight ? 'bg-[#8b9a7d] hover:bg-[#7a8a6d]' : 'bg-emerald-600 hover:bg-emerald-500'}>
                            <Plus className="w-4 h-4 mr-2" />
                            Add Genre
                          </Button>
                        )}
                      </div>
                    </div>
                    
                    {/* Existing Genres List */}
                    <div className="space-y-2 mt-4">
                      <h3 className={`text-sm font-medium ${isLight ? 'text-gray-600' : 'text-slate-300'}`}>Existing Genres ({genres.length})</h3>
                      <div className="space-y-2 max-h-[300px] overflow-y-auto">
                        {genres.map(genre => {
                          const songsInGenre = songs.filter(s => s.genre === genre.name).length;
                          const config = getGenreConfig(genre.name);
                          return (
                            <div 
                              key={genre.id}
                              className={`flex items-center justify-between p-3 rounded-lg ${isLight ? 'bg-gray-50 border border-[#ebe7e0]' : 'bg-slate-800/50'}`}
                            >
                              <div className="flex items-center gap-3">
                                {/* Show image thumbnail or gradient fallback */}
                                {genre.image ? (
                                  <img 
                                    src={getImageUrl(genre.image)} 
                                    alt={genre.name}
                                    className="w-10 h-10 rounded object-cover"
                                  />
                                ) : (
                                  <div className={`w-10 h-10 rounded bg-gradient-to-r ${genre.color || config.color}`} />
                                )}
                                <div>
                                  <span className={`font-medium ${isLight ? 'text-gray-800' : 'text-white'}`}>{genre.name}</span>
                                  <span className={`text-sm ml-2 ${isLight ? 'text-gray-400' : 'text-slate-500'}`}>({songsInGenre} songs)</span>
                                </div>
                              </div>
                              <div className="flex gap-2">
                                <Button 
                                  size="sm" 
                                  variant="ghost" 
                                  onClick={() => startEditGenre(genre)}
                                  className={isLight ? 'text-gray-400 hover:text-gray-700' : 'text-slate-400 hover:text-white'}
                                >
                                  <Edit2 className="w-4 h-4" />
                                </Button>
                                <Button 
                                  size="sm" 
                                  variant="ghost" 
                                  onClick={() => handleDeleteGenre(genre)}
                                  className="text-red-400 hover:text-red-300"
                                  disabled={songsInGenre > 0}
                                  title={songsInGenre > 0 ? 'Cannot delete: songs using this genre' : 'Delete genre'}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>
                
                <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
                  <DialogTrigger asChild>
                    <Button
                      data-testid="upload-song-button"
                      className={`rounded-full px-6 ${isLight ? 'bg-[#e94560] hover:bg-[#d63d56]' : 'bg-rose-600 hover:bg-rose-500'} text-white`}
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      Upload Song
                    </Button>
                  </DialogTrigger>

                {/* Bulk Upload Button */}
                </Dialog>
                
                <Dialog open={bulkUploadOpen} onOpenChange={(open) => {
                  if (!open) resetBulkUpload();
                  else setBulkUploadOpen(true);
                }}>
                  <DialogTrigger asChild>
                    <Button
                      data-testid="bulk-upload-button"
                      className={`rounded-full px-6 ${isLight ? 'bg-[#e94560] hover:bg-[#d63d56]' : 'bg-rose-600 hover:bg-rose-500'} text-white`}
                    >
                      <FolderUp className="w-4 h-4 mr-2" />
                      Bulk Upload
                    </Button>
                  </DialogTrigger>
                  <DialogContent className={`max-w-2xl max-h-[85vh] overflow-y-auto ${isLight ? 'bg-white border-[#ebe7e0] text-gray-800' : 'bg-slate-900 border-slate-800 text-white'}`}>
                    <DialogHeader>
                      <DialogTitle className="flex items-center gap-2">
                        <FolderUp className={`w-5 h-5 ${isLight ? 'text-[#e94560]' : 'text-violet-400'}`} />
                        Bulk Upload Songs
                      </DialogTitle>
                      <DialogDescription className={isLight ? 'text-gray-500' : 'text-slate-400'}>
                        Upload multiple songs at once. Song names are extracted from filenames.
                      </DialogDescription>
                    </DialogHeader>

                    {/* Step 1: Select Genre */}
                    {bulkUploadStep === 1 && (
                      <div className="space-y-6 py-4">
                        <div className="text-center">
                          <div className={`inline-flex items-center justify-center w-12 h-12 rounded-full mb-4 ${isLight ? 'bg-[#e94560]/10' : 'bg-rose-600/20'}`}>
                            <span className={`text-lg font-bold ${isLight ? 'text-[#e94560]' : 'text-violet-400'}`}>1</span>
                          </div>
                          <h3 className={`text-lg font-medium ${isLight ? 'text-gray-800' : 'text-white'}`}>Select or Create Genre</h3>
                          <p className={`text-sm mt-1 ${isLight ? 'text-gray-400' : 'text-slate-500'}`}>All uploaded songs will be assigned to this genre</p>
                        </div>

                        <div className="space-y-4">
                          <div>
                            <Label className={isLight ? 'text-gray-600' : ''}>Use Existing Genre</Label>
                            <Select 
                              value={bulkUploadGenre} 
                              onValueChange={(v) => { setBulkUploadGenre(v); setBulkUploadNewGenre(''); }}
                            >
                              <SelectTrigger className={`mt-1.5 ${isLight ? 'bg-[#faf8f5] border-[#ebe7e0] text-gray-800' : 'bg-slate-950 border-slate-700 text-white'}`}>
                                <SelectValue placeholder="Select a genre" />
                              </SelectTrigger>
                              <SelectContent className={isLight ? 'bg-white border-[#ebe7e0]' : 'bg-slate-900 border-slate-700'}>
                                {genres.map(g => (
                                  <SelectItem key={g.id} value={g.name} className={isLight ? 'text-gray-800' : 'text-white'}>
                                    {g.name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>

                          <div className="flex items-center gap-4">
                            <div className={`flex-1 h-px ${isLight ? 'bg-[#ebe7e0]' : 'bg-slate-700'}`} />
                            <span className={`text-sm ${isLight ? 'text-gray-400' : 'text-slate-500'}`}>OR</span>
                            <div className={`flex-1 h-px ${isLight ? 'bg-[#ebe7e0]' : 'bg-slate-700'}`} />
                          </div>

                          <div>
                            <Label className={isLight ? 'text-gray-600' : ''}>Create New Genre</Label>
                            <Input
                              value={bulkUploadNewGenre}
                              onChange={(e) => { setBulkUploadNewGenre(e.target.value); setBulkUploadGenre(''); }}
                              placeholder="Enter new genre name"
                              className={`mt-1.5 ${isLight ? 'bg-[#faf8f5] border-[#ebe7e0] text-gray-800' : 'bg-slate-950 border-slate-700 text-white'}`}
                            />
                          </div>
                        </div>

                        <div className="flex justify-end pt-4">
                          <Button
                            onClick={() => setBulkUploadStep(2)}
                            disabled={!bulkUploadGenre && !bulkUploadNewGenre}
                            className={isLight ? 'bg-[#e94560] hover:bg-[#d63d56]' : 'bg-rose-600 hover:bg-rose-500'}
                          >
                            Continue
                            <ChevronRight className="w-4 h-4 ml-2" />
                          </Button>
                        </div>
                      </div>
                    )}

                    {/* Step 2: Upload Files */}
                    {bulkUploadStep === 2 && (
                      <div className="space-y-4 py-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setBulkUploadStep(1)}
                              className={isLight ? 'text-gray-500' : 'text-slate-400'}
                            >
                              ← Back
                            </Button>
                            <div className={`px-3 py-1 rounded-full text-sm ${isLight ? 'bg-[#e94560]/10 text-[#e94560]' : 'bg-rose-600/20 text-rose-400'}`}>
                              Genre: {bulkUploadGenre || bulkUploadNewGenre}
                            </div>
                          </div>
                        </div>

                        {/* File Drop Zone */}
                        <div 
                          onClick={() => bulkFileInputRef.current?.click()}
                          className={`flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-xl cursor-pointer transition-all ${isLight ? 'border-[#ebe7e0] hover:border-[#e94560] hover:bg-[#e94560]/5' : 'border-slate-700 hover:border-rose-500 hover:bg-rose-600/5'}`}
                        >
                          <FileAudio className={`w-8 h-8 mb-2 ${isLight ? 'text-gray-400' : 'text-slate-500'}`} />
                          <p className={`text-sm ${isLight ? 'text-gray-500' : 'text-slate-400'}`}>Click to select MP3 files</p>
                          <p className={`text-xs mt-1 ${isLight ? 'text-gray-400' : 'text-slate-600'}`}>Multiple files supported</p>
                        </div>
                        <input
                          ref={bulkFileInputRef}
                          type="file"
                          accept=".mp3"
                          multiple
                          onChange={handleBulkFileSelect}
                          className="hidden"
                        />

                        {/* Selected Files List */}
                        {bulkUploadFiles.length > 0 && (
                          <div className="space-y-2">
                            <div className="flex items-center justify-between">
                              <Label className={isLight ? 'text-gray-600' : ''}>{bulkUploadFiles.length} files selected</Label>
                              <span className={`text-xs ${isLight ? 'text-gray-400' : 'text-slate-500'}`}>Artist defaults to Yume Loop</span>
                            </div>
                            <div className={`max-h-64 overflow-y-auto space-y-2 border rounded-lg p-2 ${isLight ? 'border-[#ebe7e0]' : 'border-slate-800'}`}>
                              {bulkUploadFiles.map((fileData, idx) => (
                                <div 
                                  key={idx}
                                  className={`flex items-center gap-3 p-2 rounded-lg ${
                                    fileData.status === 'success' ? 'bg-emerald-500/10 border border-emerald-500/30' :
                                    fileData.status === 'error' ? 'bg-red-500/10 border border-red-500/30' :
                                    fileData.status === 'uploading' ? (isLight ? 'bg-[#e94560]/10 border border-[#e94560]/30' : 'bg-rose-900/20 border border-rose-600/30') :
                                    (isLight ? 'bg-gray-50' : 'bg-slate-800/50')
                                  }`}
                                >
                                  <div className="w-8 h-8 rounded flex items-center justify-center flex-shrink-0">
                                    {fileData.status === 'success' ? (
                                      <Check className="w-5 h-5 text-emerald-500" />
                                    ) : fileData.status === 'error' ? (
                                      <X className="w-5 h-5 text-red-400" />
                                    ) : fileData.status === 'uploading' ? (
                                      <div className={`w-4 h-4 border-2 border-t-transparent rounded-full animate-spin ${isLight ? 'border-[#e94560]' : 'border-violet-400'}`} />
                                    ) : (
                                      <Music className={`w-5 h-5 ${isLight ? 'text-gray-400' : 'text-slate-500'}`} />
                                    )}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <Input
                                      value={fileData.name}
                                      onChange={(e) => updateBulkFile(idx, 'name', e.target.value)}
                                      className={`bg-transparent border-0 p-0 h-auto text-sm focus:ring-0 ${isLight ? 'text-gray-800' : 'text-white'}`}
                                      disabled={bulkUploading}
                                    />
                                    <div className={`flex items-center gap-2 text-xs ${isLight ? 'text-gray-400' : 'text-slate-500'}`}>
                                      <span>{fileData.artist}</span>
                                      <span>•</span>
                                      <span>{formatDuration(fileData.duration)}</span>
                                    </div>
                                  </div>
                                  {!bulkUploading && fileData.status === 'pending' && (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => removeBulkFile(idx)}
                                      className={`h-8 w-8 p-0 hover:text-red-400 ${isLight ? 'text-gray-400' : 'text-slate-500'}`}
                                    >
                                      <X className="w-4 h-4" />
                                    </Button>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Progress Bar */}
                        {bulkUploading && (
                          <div className="space-y-2">
                            <div className="flex items-center justify-between text-sm">
                              <span className={isLight ? 'text-gray-500' : 'text-slate-400'}>Uploading...</span>
                              <span className={isLight ? 'text-[#e94560]' : 'text-violet-400'}>{bulkUploadProgress}%</span>
                            </div>
                            <Progress value={bulkUploadProgress} className="h-2" />
                          </div>
                        )}

                        {/* Upload Button */}
                        <div className={`flex justify-end gap-3 pt-4 border-t ${isLight ? 'border-[#ebe7e0]' : 'border-slate-800'}`}>
                          <Button
                            variant="outline"
                            onClick={resetBulkUpload}
                            disabled={bulkUploading}
                            className={isLight ? 'border-[#ebe7e0]' : 'border-slate-700'}
                          >
                            Cancel
                          </Button>
                          <Button
                            onClick={handleBulkUpload}
                            disabled={bulkUploadFiles.length === 0 || bulkUploading}
                            className={isLight ? 'bg-[#e94560] hover:bg-[#d63d56]' : 'bg-rose-600 hover:bg-rose-500'}
                          >
                            {bulkUploading ? (
                              <>
                                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
                                Uploading...
                              </>
                            ) : (
                              <>
                                <Upload className="w-4 h-4 mr-2" />
                                Upload {bulkUploadFiles.length} Song{bulkUploadFiles.length !== 1 ? 's' : ''}
                              </>
                            )}
                          </Button>
                        </div>
                      </div>
                    )}
                  </DialogContent>
                </Dialog>

                <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
                <DialogContent className={`${isLight ? 'bg-white border-[#ebe7e0] text-gray-800' : 'bg-slate-900 border-slate-800 text-white'}`}>
                  <DialogHeader>
                    <DialogTitle className={isLight ? 'text-gray-800' : ''}>Upload New Song</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleUpload} className="space-y-4">
                    <div>
                      <Label className={isLight ? 'text-gray-600' : ''} htmlFor="file">MP3 File</Label>
                      <div className="mt-1.5">
                        <label
                          htmlFor="file-upload"
                          className={`flex items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer transition-colors ${isLight ? 'border-[#ebe7e0] hover:border-[#e94560]' : 'border-slate-700 hover:border-rose-500'}`}
                        >
                          {uploadData.file ? (
                            <div className="text-center">
                              <Music className={`w-8 h-8 mx-auto mb-2 ${isLight ? 'text-[#e94560]' : 'text-rose-400'}`} />
                              <p className={`text-sm ${isLight ? 'text-gray-600' : 'text-slate-300'}`}>{uploadData.file.name}</p>
                            </div>
                          ) : (
                            <div className="text-center">
                              <Upload className={`w-8 h-8 mx-auto mb-2 ${isLight ? 'text-gray-400' : 'text-slate-500'}`} />
                              <p className={`text-sm ${isLight ? 'text-gray-400' : 'text-slate-400'}`}>Click to upload MP3</p>
                            </div>
                          )}
                        </label>
                        <input
                          id="file-upload"
                          type="file"
                          accept=".mp3"
                          onChange={handleFileChange}
                          className="hidden"
                        />
                      </div>
                    </div>

                    <div>
                      <Label className={isLight ? 'text-gray-600' : ''} htmlFor="name">Song Name</Label>
                      <Input
                        id="name"
                        data-testid="song-name-input"
                        value={uploadData.name}
                        onChange={(e) => setUploadData({ ...uploadData, name: e.target.value })}
                        className={isLight ? 'bg-[#faf8f5] border-[#ebe7e0] text-gray-800' : 'bg-slate-950 border-slate-800 text-white'}
                        required
                      />
                    </div>

                    <div>
                      <Label className={isLight ? 'text-gray-600' : ''} htmlFor="artist">Artist</Label>
                      <Input
                        id="artist"
                        data-testid="artist-input"
                        value={uploadData.artist}
                        onChange={(e) => setUploadData({ ...uploadData, artist: e.target.value })}
                        className={isLight ? 'bg-[#faf8f5] border-[#ebe7e0] text-gray-800' : 'bg-slate-950 border-slate-800 text-white'}
                        required
                      />
                    </div>

                    <div>
                      <Label className={isLight ? 'text-gray-600' : ''} htmlFor="genre">Genre</Label>
                      <Select
                        value={uploadData.genre}
                        onValueChange={(value) => setUploadData({ ...uploadData, genre: value })}
                      >
                        <SelectTrigger className={isLight ? 'bg-[#faf8f5] border-[#ebe7e0] text-gray-800' : 'bg-slate-950 border-slate-800 text-white'}>
                          <SelectValue placeholder="Select genre" />
                        </SelectTrigger>
                        <SelectContent className={isLight ? 'bg-white border-[#ebe7e0] text-gray-800' : 'bg-slate-900 border-slate-800 text-white'}>
                          {genres.map((genre) => (
                            <SelectItem key={genre.id || genre.name} value={genre.name}>
                              {genre.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="flex gap-2 pt-4">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setUploadOpen(false)}
                        className={`flex-1 ${isLight ? 'border-[#ebe7e0]' : 'border-slate-700'}`}
                      >
                        Cancel
                      </Button>
                      <Button
                        type="submit"
                        className={`flex-1 ${isLight ? 'bg-[#e94560] hover:bg-[#d63d56]' : 'bg-rose-600 hover:bg-rose-500'}`}
                      >
                        Upload
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
            )}

            {/* Genre Cards Grid - Square Flip Cards */}
            {songs.length === 0 ? (
              <div className={`text-center py-16 rounded-2xl border ${isLight ? 'bg-white border-[#ebe7e0]' : 'bg-slate-900/40 border-slate-800'}`}>
                <Music className={`w-16 h-16 mx-auto mb-4 ${isLight ? 'text-gray-300' : 'text-slate-700'}`} />
                <p className={`mb-2 ${isLight ? 'text-gray-500' : 'text-slate-400'}`}>No songs uploaded yet</p>
                <p className={`text-sm ${isLight ? 'text-gray-400' : 'text-slate-500'}`}>Upload your first song to get started</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-5">
                {genres.map((genre) => {
                  const genreSongs = songsByGenre[genre.name] || [];
                  const config = getGenreConfig(genre.name);
                  const isExpanded = expandedGenres[genre.name];
                  const songCount = genreSongs.length;
                  
                  if (songCount === 0) return null;
                  
                  return (
                    <Collapsible 
                      key={genre.id || genre.name} 
                      open={isExpanded} 
                      onOpenChange={() => toggleGenre(genre.name)}
                      className="col-span-1"
                    >
                      <div className={`rounded-xl overflow-hidden ${isExpanded ? 'border ' + config.borderColor : ''} ${isLight ? 'shadow-md' : ''}`}>
                        {/* Genre Card - Square with Flip Effect */}
                        <CollapsibleTrigger className="w-full">
                          <div 
                            className="relative aspect-square cursor-pointer group"
                            style={{ perspective: '1000px' }}
                          >
                            <div 
                              className="relative w-full h-full transition-transform duration-500 group-hover:[transform:rotateY(180deg)]"
                              style={{ transformStyle: 'preserve-3d' }}
                            >
                              {/* Front - Image with Genre Name Tape */}
                              <div 
                                className="absolute inset-0 rounded-xl overflow-hidden"
                                style={{ backfaceVisibility: 'hidden' }}
                              >
                                <img 
                                  src={getImageUrl(genre.image) || config.image} 
                                  alt={genre.name}
                                  className="w-full h-full object-cover"
                                />
                                {/* Rose tape banner with genre name - at bottom */}
                                <div className="absolute bottom-0 left-0 right-0">
                                  <div className={`py-3 px-3 shadow-md ${isLight ? 'bg-[#e94560]' : 'bg-rose-600'}`}>
                                    <p className="text-white font-bold text-sm text-center tracking-wide uppercase">
                                      {genre.name}
                                    </p>
                                  </div>
                                </div>
                                {/* Expand indicator when already open */}
                                {isExpanded && (
                                  <div className="absolute top-2 right-2 bg-rose-500 p-1 rounded-full">
                                    <ChevronDown className="w-3 h-3 text-white" />
                                  </div>
                                )}
                              </div>
                              
                              {/* Back - Genre Name */}
                              <div 
                                className={`absolute inset-0 rounded-xl flex flex-col items-center justify-center bg-gradient-to-br ${genre.color || config.color} p-4`}
                                style={{ backfaceVisibility: 'hidden', transform: 'rotateY(180deg)' }}
                              >
                                <h3 className="text-xl font-bold text-white text-center mb-2">{genre.name}</h3>
                                <div className="flex items-center gap-1 text-white/80">
                                  <Music className="w-4 h-4" />
                                  <span className="text-sm">{songCount} song{songCount !== 1 ? 's' : ''}</span>
                                </div>
                                <div className="mt-3 text-xs text-white/60">Click to {isExpanded ? 'collapse' : 'view songs'}</div>
                              </div>
                            </div>
                          </div>
                        </CollapsibleTrigger>

                        {/* Songs List - Limited to 3 visible with scroll */}
                        <CollapsibleContent>
                          <div className={`divide-y max-h-[180px] overflow-y-auto scrollbar-thin ${isLight ? 'divide-[#ebe7e0] scrollbar-thumb-gray-300 scrollbar-track-gray-100 bg-white' : 'divide-slate-800/50 scrollbar-thumb-slate-600 scrollbar-track-slate-800/50 ' + config.bgColor} rounded-b-xl`}>
                            {genreSongs.map((song) => {
                              const isPlaying = playingId === song.id && playingType === 'song';
                              
                              return (
                                <div
                                  key={song.id}
                                  className={`p-2.5 transition-colors group ${
                                    isPlaying 
                                      ? isLight ? 'bg-[#e94560]/10' : 'bg-rose-500/10'
                                      : isLight ? 'hover:bg-gray-50' : 'hover:bg-slate-800/30'
                                  }`}
                                >
                                  <div className="flex items-center gap-2">
                                    <button
                                      onClick={() => handlePlayStop(song, 'song')}
                                      className={`w-8 h-8 rounded-full flex-shrink-0 flex items-center justify-center transition-colors ${
                                        isPlaying 
                                          ? isLight ? 'bg-[#e94560] hover:bg-[#d63d56]' : 'bg-rose-600 hover:bg-rose-500'
                                          : isLight ? 'bg-gray-200 hover:bg-gray-300' : 'bg-slate-700 hover:bg-slate-600'
                                      }`}
                                    >
                                      {isPlaying ? (
                                        <Square className={`w-3 h-3 ${isLight ? 'text-white' : 'text-white'}`} />
                                      ) : (
                                        <Play className={`w-3 h-3 ml-0.5 ${isLight ? 'text-gray-600' : 'text-white'}`} />
                                      )}
                                    </button>

                                    <div className="flex-1 min-w-0">
                                      <p 
                                        className={`text-xs font-medium leading-tight ${isPlaying ? (isLight ? 'text-[#e94560]' : 'text-rose-400') : (isLight ? 'text-gray-800' : 'text-white')}`}
                                        style={{ 
                                          display: '-webkit-box',
                                          WebkitLineClamp: 2,
                                          WebkitBoxOrient: 'vertical',
                                          overflow: 'hidden',
                                          wordBreak: 'break-word'
                                        }}
                                        title={song.name}
                                      >
                                        {song.name}
                                      </p>
                                      <p className={`text-[10px] truncate ${isLight ? 'text-gray-500' : 'text-slate-500'}`}>{song.artist} • {formatDuration(song.duration)}</p>
                                    </div>

                                    {isSuperAdmin && (
                                      <div className="flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                                        <Button
                                          onClick={() => {
                                            setSelectedSong(song);
                                            setEditOpen(true);
                                          }}
                                          variant="ghost"
                                          className="w-6 h-6 p-0"
                                        >
                                          <Edit2 className="w-2.5 h-2.5 text-slate-400" />
                                        </Button>
                                        <Button
                                          onClick={() => handleDeleteSong(song.id)}
                                          variant="ghost"
                                          className="w-6 h-6 p-0 hover:text-red-400"
                                        >
                                          <Trash2 className="w-2.5 h-2.5 text-slate-400" />
                                        </Button>
                                      </div>
                                    )}
                                  </div>
                                  
                                  {/* Audio Seeker/Waveform - shown when playing */}
                                  {isPlaying && (
                                    <div className="mt-2 ml-11">
                                      <div 
                                        className="relative h-8 bg-slate-800 rounded-lg cursor-pointer overflow-hidden"
                                        onClick={(e) => handleSeek(e, song.duration)}
                                      >
                                        {/* Waveform visualization bars */}
                                        <div className="absolute inset-0 flex items-center justify-around px-1">
                                          {Array.from({ length: 40 }, (_, i) => {
                                            const barProgress = (i / 40) * 100;
                                            const isActive = barProgress <= audioProgress;
                                            const heights = [40, 60, 80, 55, 90, 70, 45, 85, 65, 75, 50, 95, 60, 80, 70, 55, 85, 45, 90, 65, 75, 50, 80, 60, 70, 85, 55, 90, 45, 75, 65, 80, 50, 70, 60, 85, 55, 90, 75, 45];
                                            const height = heights[i % heights.length];
                                            
                                            return (
                                              <div 
                                                key={i}
                                                className={`w-1 rounded-full transition-all ${
                                                  isActive ? 'bg-rose-500' : 'bg-slate-600'
                                                }`}
                                                style={{ height: `${height}%` }}
                                              />
                                            );
                                          })}
                                        </div>
                                        
                                        {/* Progress overlay */}
                                        <div 
                                          className="absolute top-0 left-0 h-full bg-rose-500/20 pointer-events-none"
                                          style={{ width: `${audioProgress}%` }}
                                        />
                                        
                                        {/* Seek indicator */}
                                        <div 
                                          className="absolute top-0 h-full w-0.5 bg-rose-400 shadow-lg shadow-rose-500/50"
                                          style={{ left: `${audioProgress}%` }}
                                        />
                                      </div>
                                      
                                      {/* Time display */}
                                      <div className="flex justify-between text-[10px] text-slate-500 mt-1">
                                        <span>{formatDuration(Math.floor(audioCurrentTime))}</span>
                                        <span>{formatDuration(Math.floor(audioDuration || song.duration))}</span>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </CollapsibleContent>
                      </div>
                    </Collapsible>
                  );
                })}
              </div>
            )}
          </TabsContent>

          {/* ANNOUNCEMENTS TAB - Type Tiles View */}
          <TabsContent value="announcements" className="space-y-6 mt-6">
            {isSuperAdmin && (
              <div className="flex justify-end gap-3">
                {/* Manage Types Button */}
                <Dialog open={announcementTypeDialogOpen} onOpenChange={(open) => {
                  setAnnouncementTypeDialogOpen(open);
                  if (!open) resetAnnouncementTypeForm();
                }}>
                  <DialogTrigger asChild>
                    <Button
                      variant="outline"
                      className={`rounded-full px-6 ${isLight ? 'border-[#ebe7e0] text-gray-600 hover:bg-gray-50' : 'border-slate-700 text-slate-300 hover:bg-slate-800'}`}
                    >
                      <Settings className="w-4 h-4 mr-2" />
                      Manage Types
                    </Button>
                  </DialogTrigger>
                  <DialogContent className={`max-w-2xl max-h-[80vh] overflow-y-auto ${isLight ? 'bg-white border-[#ebe7e0] text-gray-800' : 'bg-slate-900 border-slate-800 text-white'}`}>
                    <DialogHeader>
                      <DialogTitle className={isLight ? 'text-gray-800' : ''}>Manage Announcement Types</DialogTitle>
                    </DialogHeader>
                    
                    {/* Add/Edit Type Form */}
                    <div className={`space-y-4 border-b pb-4 ${isLight ? 'border-[#ebe7e0]' : 'border-slate-700'}`}>
                      <h3 className={`text-sm font-medium ${isLight ? 'text-gray-600' : 'text-slate-300'}`}>
                        {editingAnnouncementType ? 'Edit Type' : 'Add New Type'}
                      </h3>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className={isLight ? 'text-gray-600' : ''}>Type Name</Label>
                          <Input
                            value={newAnnouncementTypeName}
                            onChange={(e) => setNewAnnouncementTypeName(e.target.value)}
                            placeholder="e.g., Welcome, Special Offer"
                            className={`mt-1 ${isLight ? 'bg-[#faf8f5] border-[#ebe7e0] text-gray-800' : 'bg-slate-950 border-slate-700 text-white'}`}
                          />
                        </div>
                        <div>
                          <Label className={isLight ? 'text-gray-600' : ''}>Color Theme</Label>
                          <Select value={newAnnouncementTypeColor} onValueChange={setNewAnnouncementTypeColor}>
                            <SelectTrigger className={`mt-1 ${isLight ? 'bg-[#faf8f5] border-[#ebe7e0] text-gray-800' : 'bg-slate-950 border-slate-700 text-white'}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className={isLight ? 'bg-white border-[#ebe7e0]' : 'bg-slate-900 border-slate-700'}>
                              {colorOptions.map(opt => (
                                <SelectItem key={opt.value} value={opt.value}>
                                  <div className="flex items-center gap-2">
                                    <div className={`w-4 h-4 rounded bg-gradient-to-r ${opt.value}`} />
                                    {opt.label}
                                  </div>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div>
                        <Label className={isLight ? 'text-gray-600' : ''}>Type Image</Label>
                        <div className="mt-1 space-y-2">
                          <Input
                            value={newAnnouncementTypeImage}
                            onChange={(e) => setNewAnnouncementTypeImage(e.target.value)}
                            placeholder="Paste image URL or upload below..."
                            className={`${isLight ? 'bg-[#faf8f5] border-[#ebe7e0] text-gray-800' : 'bg-slate-950 border-slate-700 text-white'}`}
                          />
                          <div className="flex items-center gap-2">
                            <input
                              type="file"
                              accept="image/*"
                              id="announcement-type-image-upload"
                              className="hidden"
                              onChange={async (e) => {
                                const file = e.target.files?.[0];
                                if (!file) return;
                                
                                const formData = new FormData();
                                formData.append('file', file);
                                
                                try {
                                  const token = localStorage.getItem('token');
                                  const response = await fetch(`${BACKEND_URL}/api/upload-image`, {
                                    method: 'POST',
                                    headers: { 'Authorization': `Bearer ${token}` },
                                    body: formData
                                  });
                                  
                                  if (response.ok) {
                                    const data = await response.json();
                                    // Store only the relative path, not the full URL
                                    setNewAnnouncementTypeImage(data.file_path);
                                    toast.success('Image uploaded successfully');
                                  } else {
                                    toast.error('Failed to upload image');
                                  }
                                } catch (error) {
                                  toast.error('Upload failed');
                                }
                              }}
                            />
                            <label
                              htmlFor="announcement-type-image-upload"
                              className={`flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer text-sm transition-colors ${isLight ? 'bg-gray-100 hover:bg-gray-200 text-gray-600' : 'bg-slate-800 hover:bg-slate-700 text-slate-300'}`}
                            >
                              <Upload className="w-4 h-4" />
                              Upload Image
                            </label>
                            {newAnnouncementTypeImage && (
                              <div className="relative w-12 h-12 rounded overflow-hidden">
                                <img src={getImageUrl(newAnnouncementTypeImage)} alt="Preview" className="w-full h-full object-cover" />
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        {editingAnnouncementType ? (
                          <>
                            <Button onClick={handleEditAnnouncementType} className={`${isLight ? 'bg-amber-500 hover:bg-amber-600' : 'bg-amber-600 hover:bg-amber-500'}`}>
                              Save Changes
                            </Button>
                            <Button variant="outline" onClick={resetAnnouncementTypeForm} className={`${isLight ? 'border-[#ebe7e0]' : 'border-slate-700'}`}>
                              Cancel
                            </Button>
                          </>
                        ) : (
                          <Button onClick={handleAddAnnouncementType} className={`${isLight ? 'bg-emerald-500 hover:bg-emerald-600' : 'bg-emerald-600 hover:bg-emerald-500'}`}>
                            <Plus className="w-4 h-4 mr-2" />
                            Add Type
                          </Button>
                        )}
                      </div>
                    </div>
                    
                    {/* Existing Types List */}
                    <div className="space-y-2 mt-4">
                      <h3 className={`text-sm font-medium ${isLight ? 'text-gray-600' : 'text-slate-300'}`}>Existing Types</h3>
                      {announcementTypes.map((type) => {
                        const config = getAnnouncementConfig(type);
                        const typeCount = announcements.filter(a => (a.genre || 'General') === type).length;
                        
                        return (
                          <div
                            key={type}
                            className={`flex items-center gap-3 p-3 rounded-lg group ${isLight ? 'bg-gray-50 border border-[#ebe7e0]' : 'bg-slate-800/50'}`}
                          >
                            <div className="relative w-10 h-10 rounded overflow-hidden">
                              <img 
                                src={getImageUrl(config.image)} 
                                alt={type}
                                className="w-full h-full object-cover"
                              />
                            </div>
                            <div className="flex-1">
                              <p className={`font-medium ${isLight ? 'text-gray-800' : 'text-white'}`}>{type}</p>
                              <p className={`text-xs ${isLight ? 'text-gray-500' : 'text-slate-400'}`}>{typeCount} announcement(s)</p>
                            </div>
                            <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                              <Button
                                variant="ghost"
                                className={`w-8 h-8 p-0 ${isLight ? 'hover:bg-gray-200' : ''}`}
                                onClick={() => {
                                  setEditingAnnouncementType(type);
                                  setNewAnnouncementTypeName(type);
                                  setNewAnnouncementTypeImage(config.image || '');
                                  setNewAnnouncementTypeColor(config.color || 'from-amber-500 to-orange-600');
                                }}
                              >
                                <Edit2 className={`w-4 h-4 ${isLight ? 'text-gray-500' : 'text-slate-400'}`} />
                              </Button>
                              <Button
                                variant="ghost"
                                className={`w-8 h-8 p-0 hover:text-red-400 ${isLight ? 'hover:bg-red-50' : ''}`}
                                onClick={() => handleDeleteAnnouncementType(type)}
                              >
                                <Trash2 className={`w-4 h-4 ${isLight ? 'text-gray-500' : 'text-slate-400'}`} />
                              </Button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </DialogContent>
                </Dialog>

                {/* Upload Announcement Button */}
                <Dialog open={uploadAnnouncementOpen} onOpenChange={setUploadAnnouncementOpen}>
                  <DialogTrigger asChild>
                    <Button className={`rounded-full px-6 ${isLight ? 'bg-[#e94560] hover:bg-[#d63d56]' : 'bg-amber-600 hover:bg-amber-500'} text-white`}>
                      <Upload className="w-4 h-4 mr-2" />
                      Upload Announcement
                  </Button>
                </DialogTrigger>
                <DialogContent className={isLight ? 'bg-white border-[#ebe7e0] text-gray-800' : 'bg-slate-900 border-slate-800 text-white'}>
                  <DialogHeader>
                    <DialogTitle className={isLight ? 'text-gray-800' : ''}>Upload Voice Announcement</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleUploadAnnouncement} className="space-y-4">
                    <div>
                      <Label className={isLight ? 'text-gray-600' : ''}>MP3 File</Label>
                      <div className="mt-1.5">
                        <label
                          htmlFor="announcement-file-upload"
                          className={`flex items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer transition-colors ${isLight ? 'border-[#ebe7e0] hover:border-[#e94560]' : 'border-slate-700 hover:border-amber-500'}`}
                        >
                          {announcementData.file ? (
                            <div className="text-center">
                              <Mic className={`w-8 h-8 mx-auto mb-2 ${isLight ? 'text-[#e94560]' : 'text-amber-400'}`} />
                              <p className={`text-sm ${isLight ? 'text-gray-600' : 'text-slate-300'}`}>{announcementData.file.name}</p>
                            </div>
                          ) : (
                            <div className="text-center">
                              <Upload className={`w-8 h-8 mx-auto mb-2 ${isLight ? 'text-gray-400' : 'text-slate-500'}`} />
                              <p className={`text-sm ${isLight ? 'text-gray-400' : 'text-slate-400'}`}>Click to upload MP3</p>
                            </div>
                          )}
                        </label>
                        <input
                          id="announcement-file-upload"
                          type="file"
                          accept=".mp3"
                          onChange={handleAnnouncementFileChange}
                          className="hidden"
                        />
                      </div>
                    </div>

                    <div>
                      <Label className={isLight ? 'text-gray-600' : ''}>Announcement Name</Label>
                      <Input
                        value={announcementData.name}
                        onChange={(e) => setAnnouncementData({ ...announcementData, name: e.target.value })}
                        placeholder="e.g., Store Closing Notice"
                        className={isLight ? 'bg-[#faf8f5] border-[#ebe7e0] text-gray-800' : 'bg-slate-950 border-slate-800 text-white'}
                        required
                      />
                    </div>

                    <div>
                      <Label className={isLight ? 'text-gray-600' : ''}>Type</Label>
                      <Select
                        value={announcementData.genre}
                        onValueChange={(value) => setAnnouncementData({ ...announcementData, genre: value })}
                      >
                        <SelectTrigger className={isLight ? 'bg-[#faf8f5] border-[#ebe7e0] text-gray-800' : 'bg-slate-950 border-slate-800 text-white'}>
                          <SelectValue placeholder="Select type" />
                        </SelectTrigger>
                        <SelectContent className={isLight ? 'bg-white border-[#ebe7e0] text-gray-800' : 'bg-slate-900 border-slate-800 text-white'}>
                          {announcementTypes.map((type) => (
                            <SelectItem key={type} value={type}>
                              {type}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {announcementData.duration > 0 && (
                      <div className={`text-sm ${isLight ? 'text-gray-500' : 'text-slate-400'}`}>
                        Duration: {formatDuration(announcementData.duration)}
                      </div>
                    )}

                    <div className="flex gap-2 pt-4">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setUploadAnnouncementOpen(false)}
                        className={`flex-1 ${isLight ? 'border-[#ebe7e0]' : 'border-slate-700'}`}
                      >
                        Cancel
                      </Button>
                      <Button
                        type="submit"
                        className={`flex-1 ${isLight ? 'bg-[#e94560] hover:bg-[#d63d56]' : 'bg-amber-600 hover:bg-amber-500'}`}
                      >
                        Upload
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>

              {/* Upload Background Music Button */}
              <Dialog open={uploadBackgroundMusicOpen} onOpenChange={setUploadBackgroundMusicOpen}>
                <DialogTrigger asChild>
                  <Button className={`rounded-full px-6 ${isLight ? 'bg-[#e94560] hover:bg-[#d63d56]' : 'bg-amber-600 hover:bg-amber-500'} text-white`}>
                    <Music className="w-4 h-4 mr-2" />
                    Upload Background Music
                  </Button>
                </DialogTrigger>
                <DialogContent className={isLight ? 'bg-white border-[#ebe7e0] text-gray-800' : 'bg-slate-900 border-slate-800 text-white'}>
                  <DialogHeader>
                    <DialogTitle className={isLight ? 'text-gray-800' : ''}>Upload Background Music</DialogTitle>
                  </DialogHeader>
                  <p className={`text-sm mb-4 ${isLight ? 'text-gray-500' : 'text-slate-400'}`}>
                    Upload music that can be used as background for Text-to-Voice announcements.
                  </p>
                  <form onSubmit={handleUploadBackgroundMusic} className="space-y-4">
                    <div>
                      <Label className={isLight ? 'text-gray-600' : ''}>MP3 File</Label>
                      <div className="mt-1.5">
                        <label
                          htmlFor="bg-music-file-upload"
                          className={`flex items-center justify-center w-full h-24 border-2 border-dashed rounded-lg cursor-pointer transition-colors ${isLight ? 'border-[#ebe7e0] hover:border-[#e94560]' : 'border-slate-700 hover:border-cyan-500'}`}
                        >
                          {backgroundMusicData.file ? (
                            <div className="text-center">
                              <Music className={`w-6 h-6 mx-auto mb-1 ${isLight ? 'text-[#e94560]' : 'text-cyan-400'}`} />
                              <p className={`text-sm ${isLight ? 'text-gray-600' : 'text-slate-300'}`}>{backgroundMusicData.file.name}</p>
                            </div>
                          ) : (
                            <div className="text-center">
                              <Upload className={`w-6 h-6 mx-auto mb-1 ${isLight ? 'text-gray-400' : 'text-slate-500'}`} />
                              <p className={`text-sm ${isLight ? 'text-gray-400' : 'text-slate-400'}`}>Click to upload MP3</p>
                            </div>
                          )}
                        </label>
                        <input
                          id="bg-music-file-upload"
                          type="file"
                          accept=".mp3"
                          onChange={(e) => setBackgroundMusicData(prev => ({ ...prev, file: e.target.files[0] }))}
                          className="hidden"
                        />
                      </div>
                    </div>
                    <div>
                      <Label className={isLight ? 'text-gray-600' : ''}>Name</Label>
                      <Input
                        value={backgroundMusicData.name}
                        onChange={(e) => setBackgroundMusicData(prev => ({ ...prev, name: e.target.value }))}
                        placeholder="e.g., Soft Piano Loop"
                        className={`mt-1 ${isLight ? 'bg-[#faf8f5] border-[#ebe7e0] text-gray-800' : 'bg-slate-950 border-slate-800 text-white'}`}
                        required
                      />
                    </div>
                    <div className="flex gap-2 pt-2">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setUploadBackgroundMusicOpen(false)}
                        className={`flex-1 ${isLight ? 'border-[#ebe7e0]' : 'border-slate-700'}`}
                      >
                        Cancel
                      </Button>
                      <Button
                        type="submit"
                        className={`flex-1 ${isLight ? 'bg-[#e94560] hover:bg-[#d63d56]' : 'bg-cyan-600 hover:bg-cyan-500'}`}
                      >
                        Upload
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
            )}

            {/* Background Music Section */}
            {isSuperAdmin && backgroundMusic.length > 0 && (
              <div className="mb-6">
                <h3 className={`text-sm font-medium mb-3 flex items-center gap-2 ${isLight ? 'text-gray-600' : 'text-slate-400'}`}>
                  <Music className="w-4 h-4" />
                  Background Music for TTS ({backgroundMusic.length})
                </h3>
                <div className="flex flex-wrap gap-2">
                  {backgroundMusic.map((bg) => (
                    <div
                      key={bg.id}
                      className={`flex items-center gap-2 px-3 py-2 rounded-lg border ${isLight ? 'bg-white border-[#ebe7e0]' : 'bg-slate-800/50 border-slate-700/50'} group`}
                    >
                      <Button
                        variant="ghost"
                        size="sm"
                        className={`w-7 h-7 p-0 rounded-full ${
                          playingId === bg.id && playingType === 'background' 
                            ? (isLight ? 'bg-[#e94560]/20 text-[#e94560]' : 'bg-cyan-500/20 text-cyan-400')
                            : (isLight ? 'hover:bg-[#e94560]/20 text-[#e94560]' : 'hover:bg-cyan-500/20 text-cyan-400')
                        }`}
                        onClick={() => handlePlayStop(bg, 'background')}
                        data-testid={`play-bg-${bg.id}`}
                      >
                        {playingId === bg.id && playingType === 'background' ? (
                          <Square className="w-3 h-3" />
                        ) : (
                          <Play className="w-3 h-3" />
                        )}
                      </Button>
                      <span className={`text-sm ${isLight ? 'text-gray-700' : 'text-white'}`}>{bg.name}</span>
                      <span className="text-xs text-slate-500">{formatDuration(bg.duration || 0)}</span>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="w-6 h-6 p-0 opacity-0 group-hover:opacity-100 hover:text-red-400"
                        onClick={() => handleDeleteBackgroundMusic(bg.id)}
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Announcement Type Tiles - Flip Cards with Images */}
            {announcements.length === 0 ? (
              <div className={`text-center py-16 rounded-2xl border ${isLight ? 'bg-white border-[#ebe7e0]' : 'bg-slate-900/40 border-slate-800'}`}>
                <Mic className={`w-16 h-16 mx-auto mb-4 ${isLight ? 'text-gray-300' : 'text-slate-700'}`} />
                <p className={`mb-2 ${isLight ? 'text-gray-500' : 'text-slate-400'}`}>No announcements uploaded yet</p>
                <p className={`text-sm ${isLight ? 'text-gray-400' : 'text-slate-500'}`}>Upload your first announcement to get started</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                {announcementTypes.map((type) => {
                  const typeAnnouncements = announcementsByType[type];
                  const config = getAnnouncementConfig(type);
                  const isExpanded = expandedAnnouncementTypes[type];
                  const count = typeAnnouncements.length;
                  
                  if (count === 0) return null;
                  
                  return (
                    <Collapsible 
                      key={type} 
                      open={isExpanded} 
                      onOpenChange={() => toggleAnnouncementType(type)}
                    >
                      <div className={`rounded-xl overflow-hidden ${isExpanded ? 'border ' + config.borderColor : ''}`}>
                        {/* Card Front - Flip Card Style with Image */}
                        <CollapsibleTrigger asChild>
                          <div 
                            className="relative aspect-square cursor-pointer group"
                            style={{ perspective: '1000px' }}
                          >
                            <div
                              className={`relative w-full h-full transition-transform duration-500 ${
                                isExpanded ? '' : 'group-hover:scale-[1.02]'
                              }`}
                              style={{ transformStyle: 'preserve-3d' }}
                            >
                              {/* Front - Image */}
                              <div 
                                className="absolute inset-0 rounded-xl overflow-hidden"
                                style={{ backfaceVisibility: 'hidden' }}
                              >
                                <img 
                                  src={getImageUrl(config.image)}
                                  alt={type}
                                  className="w-full h-full object-cover"
                                />
                                {/* Transparent banner with type name - at bottom */}
                                <div className="absolute bottom-0 left-0 right-0">
                                  <div className="bg-amber-500/50 py-3 px-3 backdrop-blur-sm">
                                    <p className="text-white font-bold text-sm text-center tracking-wide uppercase drop-shadow-md">
                                      {type}
                                    </p>
                                  </div>
                                </div>
                                {/* Expand indicator when already open */}
                                {isExpanded && (
                                  <div className="absolute top-2 right-2 bg-amber-500 p-1 rounded-full">
                                    <ChevronDown className="w-3 h-3 text-white" />
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </CollapsibleTrigger>

                        {/* Announcements List - Limited to 3 visible with scroll */}
                        <CollapsibleContent>
                          <div className={`divide-y max-h-[180px] overflow-y-auto scrollbar-thin ${isLight ? 'divide-[#ebe7e0] scrollbar-thumb-gray-300 scrollbar-track-gray-100 bg-white' : 'divide-slate-800/50 scrollbar-thumb-slate-600 scrollbar-track-slate-800/50 ' + config.bgColor} rounded-b-xl`}>
                            {typeAnnouncements.map((announcement) => {
                              const isPlaying = playingId === announcement.id && playingType === 'announcement';
                              
                              return (
                                <div
                                  key={announcement.id}
                                  className={`p-2.5 transition-colors group ${
                                    isPlaying 
                                      ? isLight ? 'bg-amber-500/10' : 'bg-amber-500/10'
                                      : isLight ? 'hover:bg-gray-50' : 'hover:bg-slate-800/30'
                                  }`}
                                >
                                  <div className="flex items-center gap-2">
                                    <button
                                      onClick={() => handlePlayStop(announcement, 'announcement')}
                                      className={`w-8 h-8 rounded-full flex-shrink-0 flex items-center justify-center transition-colors ${
                                        isPlaying 
                                          ? 'bg-amber-600 hover:bg-amber-500' 
                                          : isLight ? 'bg-gray-200 hover:bg-gray-300' : 'bg-slate-700 hover:bg-slate-600'
                                      }`}
                                    >
                                      {isPlaying ? (
                                        <Square className="w-3 h-3 text-white" />
                                      ) : (
                                        <Play className={`w-3 h-3 ml-0.5 ${isLight ? 'text-gray-600' : 'text-white'}`} />
                                      )}
                                    </button>

                                    <div className="flex-1 min-w-0">
                                      <p 
                                        className={`text-xs font-medium leading-tight ${isPlaying ? 'text-amber-500' : (isLight ? 'text-gray-800' : 'text-white')}`}
                                        style={{ 
                                          display: '-webkit-box',
                                          WebkitLineClamp: 2,
                                          WebkitBoxOrient: 'vertical',
                                          overflow: 'hidden',
                                          wordBreak: 'break-word'
                                        }}
                                        title={announcement.name}
                                      >
                                        {announcement.name}
                                      </p>
                                      <p className={`text-[10px] truncate ${isLight ? 'text-gray-500' : 'text-slate-500'}`}>{formatDuration(announcement.duration || 0)}</p>
                                    </div>

                                    {isSuperAdmin && (
                                      <Button
                                        onClick={() => handleDeleteAnnouncement(announcement.id)}
                                        variant="ghost"
                                        className="w-6 h-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity hover:text-red-400"
                                      >
                                        <Trash2 className={`w-2.5 h-2.5 ${isLight ? 'text-gray-400' : 'text-slate-400'}`} />
                                      </Button>
                                    )}
                                  </div>
                                  
                                  {/* Audio Seeker/Waveform - shown when playing */}
                                  {isPlaying && (
                                    <div className="mt-2 ml-10">
                                      <div 
                                        className={`relative h-8 rounded-lg cursor-pointer overflow-hidden ${isLight ? 'bg-gray-200' : 'bg-slate-800'}`}
                                        onClick={(e) => handleSeek(e, announcement.duration)}
                                      >
                                        {/* Waveform visualization bars */}
                                        <div className="absolute inset-0 flex items-center justify-around px-1">
                                          {Array.from({ length: 30 }, (_, i) => {
                                            const barProgress = (i / 30) * 100;
                                            const isActive = barProgress <= audioProgress;
                                            const height = 20 + Math.sin(i * 0.5) * 15 + Math.random() * 10;
                                            
                                            return (
                                              <div
                                                key={i}
                                                className={`w-1 rounded-full transition-colors ${
                                                  isActive ? 'bg-amber-500' : (isLight ? 'bg-gray-400' : 'bg-slate-600')
                                                }`}
                                                style={{ height: `${height}%` }}
                                              />
                                            );
                                          })}
                                        </div>
                                        {/* Progress overlay */}
                                        <div 
                                          className="absolute top-0 left-0 h-full bg-amber-500/20 pointer-events-none"
                                          style={{ width: `${audioProgress}%` }}
                                        />
                                      </div>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </CollapsibleContent>
                      </div>
                    </Collapsible>
                  );
                })}
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Edit Song Dialog */}
        <Dialog open={editOpen} onOpenChange={setEditOpen}>
          <DialogContent className={`${isLight ? 'bg-white border-[#ebe7e0] text-gray-800' : 'bg-slate-900 border-slate-800 text-white'}`}>
            <DialogHeader>
              <DialogTitle className={isLight ? 'text-gray-800' : ''}>Edit Song</DialogTitle>
            </DialogHeader>
            {selectedSong && (
              <form onSubmit={handleEdit} className="space-y-4">
                <div>
                  <Label className={isLight ? 'text-gray-600' : ''}>Song Name</Label>
                  <Input
                    value={selectedSong.name}
                    onChange={(e) => setSelectedSong({ ...selectedSong, name: e.target.value })}
                    className={isLight ? 'bg-[#faf8f5] border-[#ebe7e0] text-gray-800' : 'bg-slate-950 border-slate-800 text-white'}
                    required
                  />
                </div>

                <div>
                  <Label className={isLight ? 'text-gray-600' : ''}>Artist</Label>
                  <Input
                    value={selectedSong.artist}
                    onChange={(e) => setSelectedSong({ ...selectedSong, artist: e.target.value })}
                    className={isLight ? 'bg-[#faf8f5] border-[#ebe7e0] text-gray-800' : 'bg-slate-950 border-slate-800 text-white'}
                    required
                  />
                </div>

                <div>
                  <Label className={isLight ? 'text-gray-600' : ''}>Genre</Label>
                  <Select
                    value={selectedSong.genre}
                    onValueChange={(value) => setSelectedSong({ ...selectedSong, genre: value })}
                  >
                    <SelectTrigger className={isLight ? 'bg-[#faf8f5] border-[#ebe7e0] text-gray-800' : 'bg-slate-950 border-slate-800 text-white'}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className={isLight ? 'bg-white border-[#ebe7e0] text-gray-800' : 'bg-slate-900 border-slate-800 text-white'}>
                      {genres.map((genre) => (
                        <SelectItem key={genre.id || genre.name} value={genre.name}>
                          {genre.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex gap-2 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setEditOpen(false)}
                    className={`flex-1 ${isLight ? 'border-[#ebe7e0]' : 'border-slate-700'}`}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    className={`flex-1 ${isLight ? 'bg-[#e94560] hover:bg-[#d63d56]' : 'bg-rose-600 hover:bg-rose-500'}`}
                  >
                    Save Changes
                  </Button>
                </div>
              </form>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default MusicLibrary;
